// Buildings family: ApiService building reads -> NomOS `site` records. Reshape only; no client-side aggregates.
import type { Building, BuildingIndexResponse, BuildingListResponse } from '@/types';
import { attributeIds, registerTenantCache, groupRecords, nomosFetch, queryRecords, refs, scalar, toAxiosError, type NomosEnvelope, type NomosFilter, type NomosPage, type NomosRow } from '../client';
import { localDate, localMidnight, tenantZone } from '../tz';
import { rememberId } from '../ids';
import type { AnyFn, NomosRegistry } from '../registry';
import { climateZoneCodes, countryCodes, countryRecordId } from './lookups';

// The meters/assets/initiatives relation cells are not read: every caller counts them with pageExtras (NomOS group_by).
const LIST_FIELDS = ['name', 'building_type', 'location', 'country_ref', 'size_sqm', 'sentinel_building_id', 'timezone',
  'opening_hours', 'climate_zone', 'store_manager', 'eui_per_building',
  'is_active', 'operational_start_date', 'operational_end_date', 'state_province', 'google_place_id', 'shelly_configured',
  'sales_area_sqm', 'stockroom_area_sqm', 'social_area_sqm', 'other_area_sqm', 'lease', 'contacts', 'building_attributes',
  'external_system_ids', 'store_manager.email'];
const MANAGER_EMAIL = 'store_manager.email';
const SORT_MAP: Record<string, string> = { name: 'name', size_sqm: 'size_sqm', building_type: 'building_type', eui_kwh_per_sqm: 'eui_per_building' };
const ACTIVE: NomosFilter = { attribute: 'is_active', op: 'eq', value: true };
/** Sort fields that are CALCULATED on NomOS: no stored cell to order by, so records/query leaves the order as is (the
 *  same order both ways). These sort in the adapter over the filtered set's values, then page. */
const CALCULATED_SORTS = new Set(['eui_per_building']);
const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']; // Google periods: 0 = Sunday

type GetBuildingsParams = { page?: number; page_size?: number; limit?: number; search?: string; country?: string;
  building_type?: string; active_only?: boolean; start_date?: string; end_date?: string; sort?: string; order?: 'asc' | 'desc' };

const num = (x: unknown) => (typeof x === 'number' ? x : typeof x === 'string' && x !== '' ? Number(x) : undefined);
const str = (x: unknown): string | null => { const s = scalar(x); return typeof s === 'string' && s !== '' ? s : null; };
const bool = (x: unknown): boolean | undefined => { const s = scalar(x); return typeof s === 'boolean' ? s : undefined; };
/** JSON attribute cell: an object (or list of one) -> object; empty -> undefined. */
const json = (x: unknown): unknown => { const c = Array.isArray(x) ? x[0] : x; return c && typeof c === 'object' && !('value' in (c as object)) ? c : undefined; };
const hhmm = (t: unknown) => (typeof t === 'string' && t.length === 4 ? `${t.slice(0, 2)}:${t.slice(2)}` : null);

/** EUI window as Sentinel resolves it: [start_date | today-365d, end_date | yesterday] as whole days of the tenant calendar
 *  (local midnights, as NomOS buckets days; [start, end+1d)). */
async function euiPeriod(p?: GetBuildingsParams): Promise<{ start: string; end: string }> {
  const zone = await tenantZone();
  const shift = (ymd: string, days: number) => new Date(Date.parse(`${ymd}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
  const today = localDate(new Date(), zone);
  const start = p?.start_date ?? shift(today, -365);
  const endIncl = p?.end_date ?? shift(today, -1);
  return { start: localMidnight(start, zone), end: localMidnight(shift(endIncl, 1), zone) };
}

function openingHours(v: unknown): Record<string, { open: string | null; close: string | null } | null> | null {
  const periods = (v as { periods?: Array<{ open?: { day: number; time: string }; close?: { time: string } }> } | null)?.periods;
  if (!periods?.length) return null;
  const out: Record<string, { open: string | null; close: string | null } | null> = {};
  for (const d of DAYS) out[d] = null as never; // Sentinel: a closed day is null
  for (const p of periods) if (p.open) out[DAYS[p.open.day]] = { open: hhmm(p.open.time), close: hhmm(p.close?.time) };
  return out;
}

interface Counts { inProgress: Map<string, number>; initiatives: Map<string, number>; devices: Map<string, number>; meters: Map<string, number> }
interface Extras extends Counts { emails: Map<string, string> }

/** person.email of the given people only (records/values for those ids, one call per 1000). Older NomOS only: a
 *  projecting NomOS answers `store_manager.email` with the list itself. */
async function personEmails(ids: string[]): Promise<Map<string, string>> {
  const emails = new Map<string, string>();
  if (!ids.length) return emails;
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += 1000) chunks.push(ids.slice(i, i + 1000));
  const [pages, attr] = await Promise.all([
    Promise.all(chunks.map((c) => nomosFetch<NomosEnvelope<{ values?: Record<string, Record<string, unknown>> }>>(
      'POST', '/objects/person/records/values', { record_ids: c, attributes: ['email'] }))),
    attributeIds('person'),
  ]);
  for (const vr of pages) {
    for (const [rid, cells] of Object.entries(vr.data?.values ?? {})) {
      const e = scalar(cells[attr.email]);
      if (typeof e === 'string') emails.set(rid, e);
    }
  }
  return emails;
}

/**
 * Per-site counts counted by NomOS (group_by site), independent of the page's rows so they travel in the SAME round
 * trip as the list: the whole tenant (818 sites group in ~0.26 s; a large `site in [...]` is ~30x slower), or one
 * building through the reference path `site.sentinel_building_id`.
 */
function siteCounts(oneBuilding?: string): Promise<Counts> {
  const bySite = async (type: string, filter: NomosFilter) => {
    const f: NomosFilter = oneBuilding ? { and: [filter, { attribute: 'site.sentinel_building_id', op: 'eq', value: oneBuilding }] } : filter;
    const m = new Map<string, number>();
    for (const g of await groupRecords(type, { groupBy: ['site'], filter: f })) m.set(String(g.key[0]), g.count);
    return m;
  };
  return Promise.all([
    bySite('initiative', { attribute: 'status', op: 'eq', value: 'in_progress' }),
    bySite('initiative', { attribute: 'status', op: 'ne', value: 'cancelled' }),   // Sentinel: initiatives excluding cancelled
    bySite('asset', { attribute: 'role', op: 'in', value: ['metering', 'actuating'] }), // Sentinel: device_count = metering/actuating assets
    bySite('meter', { attribute: 'is_active', op: 'eq', value: true }), // Sentinel: meter_point_count = active meter points
  ]).then(([inProgress, initiatives, devices, meters]) => ({ inProgress, initiatives, devices, meters }));
}

/** The store manager's e-mail as the list projected it (`store_manager.email`: a path group, the leaf value). */
function projectedEmail(r: NomosRow): string | undefined {
  const cell = r.values[MANAGER_EMAIL] as { groups?: Array<{ value?: unknown }> } | undefined;
  const v = cell?.groups?.[0]?.value;
  const e = scalar(v);
  return typeof e === 'string' ? e : undefined;
}

/** Counts (started with the list) + manager e-mails (from the list's own projection; a lookup only on an older NomOS). */
async function pageExtras(rows: NomosRow[], counts: Promise<Counts>): Promise<Extras> {
  const emails = new Map<string, string>();
  const missing: string[] = [];
  for (const r of rows) {
    const m = refs(r.values.store_manager)[0]?.id;
    if (!m) continue;
    const e = projectedEmail(r);
    if (e) emails.set(m, e); else if (!(MANAGER_EMAIL in r.values)) missing.push(m);
  }
  const [c, more] = await Promise.all([counts, personEmails([...new Set(missing)])]);
  more.forEach((v, k) => emails.set(k, v));
  return { ...c, emails };
}

let emissionFactors: Promise<Array<{ country: string; source: string; value: number; from: string; to: string | null }>> | undefined;
registerTenantCache(() => { emissionFactors = undefined; });
/** Grid electricity factor valid today for a country (Sentinel emission_factor_service.lookup_sync); 0.26 EU fallback as Sentinel. */
async function co2PerKwh(countryCode: string | null): Promise<number> {
  emissionFactors ??= (async () => {
    const [countries, res] = await Promise.all([countryCodes(),
      queryRecords('emission_factor', { fields: ['country_ref', 'energy_source', 'emission_factor', 'valid_from', 'valid_to'], limit: 1000 })]);
    return res.rows.map((r) => ({ country: countries.get(refs(r.values.country_ref)[0]?.id ?? '') ?? '', source: refs(r.values.energy_source)[0]?.name ?? '',
      value: num(scalar(r.values.emission_factor)) ?? NaN, from: str(r.values.valid_from) ?? '0000-01-01', to: str(r.values.valid_to) }));
  })();
  emissionFactors.catch(() => { emissionFactors = undefined; });
  const today = new Date().toISOString().slice(0, 10);
  const hits = (await emissionFactors).filter((f) => f.country === (countryCode ?? 'DK').toUpperCase() && /electricity/i.test(f.source)
    && f.from <= today && (!f.to || f.to >= today) && !isNaN(f.value)).sort((a, b) => b.from.localeCompare(a.from));
  return hits[0]?.value ?? 0.26;
}

async function toBuilding(r: NomosRow, x?: Extras): Promise<Building> {
  const v = r.values;
  const [countries, zones] = await Promise.all([countryCodes(), climateZoneCodes()]);
  const sid = scalar(v.sentinel_building_id);
  const id = typeof sid === 'string' ? sid : r.record_id;
  if (typeof sid === 'string') rememberId('building', sid, r.record_id);
  const loc = (Array.isArray(v.location) ? v.location[0] : v.location) as Record<string, unknown> | null | undefined;
  const country = refs(v.country_ref)[0];
  const zone = refs(v.climate_zone)[0];
  const manager = refs(v.store_manager)[0];
  const count = (cell: unknown) => (cell as { count?: number } | undefined)?.count ?? 0;
  const eui = num(scalar(v.eui_per_building));
  const today = new Date().toISOString().slice(0, 10);
  const start = str(v.operational_start_date);
  const end = str(v.operational_end_date);
  return {
    id,
    name: (scalar(v.name) as string) ?? r.kenning,
    street_address: (loc?.street as string) ?? null,
    city: (loc?.city as string) ?? null,
    postal_code: (loc?.postal_code as string) ?? null,
    country_code: (loc?.country as string) ?? (country ? countries.get(country.id) : undefined) ?? null,
    latitude: num(loc?.latitude) ?? null,
    longitude: num(loc?.longitude) ?? null,
    size_sqm: num(scalar(v.size_sqm)) ?? null,
    building_type: (scalar(v.building_type) as string) ?? null,
    timezone: (scalar(v.timezone) as string) ?? null,
    opening_hours: openingHours(v.opening_hours),
    climate_zone: zone ? zones.get(zone.id) ?? null : null,
    store_manager_name: manager?.name ?? null,
    store_manager_email: (manager && x?.emails.get(manager.id)) ?? null,
    initiatives_in_progress: x?.inProgress.get(r.record_id) ?? 0,
    co2_per_kwh: await co2PerKwh(((loc?.country as string) ?? (country ? countries.get(country.id) : undefined)) ?? null),
    device_count: x ? x.devices.get(r.record_id) ?? 0 : count(v.assets),
    meter_point_count: x ? x.meters.get(r.record_id) ?? 0 : count(v.meters),
    initiative_count: x ? x.initiatives.get(r.record_id) ?? 0 : count(v.initiatives),
    eui_kwh_per_sqm: eui === undefined ? null : eui,
    lease_count: 0, // Sentinel hard-codes 0 (routers/buildings.py list query)
    state_province: str(v.state_province) ?? (loc?.state as string) ?? '',
    google_place_id: str(v.google_place_id),
    external_system_ids: json(v.external_system_ids) ?? {},
    is_active: bool(v.is_active) ?? true,
    shelly_configured: bool(v.shelly_configured) ?? false,
    operational_start_date: start,
    operational_end_date: end,
    // derived against today exactly as Sentinel does (routers/buildings.py _row_to_response)
    is_planned: start !== null && start > today,
    is_closed: end !== null && end < today,
    sales_area_sqm: num(scalar(v.sales_area_sqm)) ?? null,
    stockroom_area_sqm: num(scalar(v.stockroom_area_sqm)) ?? null,
    social_area_sqm: num(scalar(v.social_area_sqm)) ?? null,
    other_area_sqm: num(scalar(v.other_area_sqm)) ?? null,
    lease: json(v.lease) ?? null,
    contacts: json(v.contacts) ?? null,
    building_attributes: json(v.building_attributes) ?? null,
  } as unknown as Building;
}

async function listSites(params: GetBuildingsParams | undefined, forceLimit?: number) {
  const page_size = forceLimit ?? (params?.limit || params?.page_size || 50);
  const page = params?.page || 1;
  const clauses: NomosFilter[] = [];
  const period = euiPeriod(params); // independent of the filter lookups below: started first, awaited at the query
  period.catch(() => undefined);
  if (params?.search) {
    clauses.push({ or: [
      { attribute: 'name', op: 'contains', value: params.search },
      { attribute: 'location.city', op: 'contains', value: params.search },
      { attribute: 'location.street', op: 'contains', value: params.search },
    ] });
  }
  if (params?.building_type) clauses.push({ attribute: 'building_type', op: 'eq', value: params.building_type.toLowerCase() });
  if (params?.country) {
    const cid = await countryRecordId(params.country);
    clauses.push(cid ? { attribute: 'country_ref', op: 'eq', value: cid } : { attribute: 'name', op: 'eq', value: '\u0000no-such-country' });
  }
  if (params?.active_only !== false) clauses.push({ attribute: 'is_active', op: 'eq', value: true });
  const sortField = params?.sort ? SORT_MAP[params.sort] : 'name';
  const filter = clauses.length === 1 ? clauses[0] : clauses.length ? { and: clauses } : undefined;
  if (sortField && CALCULATED_SORTS.has(sortField)) {
    return { res: await calculatedSortPage(filter, sortField, params?.order ?? 'asc', page, page_size, await period), page, page_size };
  }
  const res = await queryRecords('site', {
    filter, fields: LIST_FIELDS, limit: page_size, cursor: (page - 1) * page_size, period: await period,
    sorts: sortField ? [{ field: sortField, direction: params?.order ?? 'asc' }] : undefined,
  });
  return { res, page, page_size };
}

/** Number order with empty values (null, undefined, NaN) LAST in both directions; ties keep their input order. */
export function compareNumbersNullsLast(a: number | null | undefined, b: number | null | undefined, order: 'asc' | 'desc'): number {
  const na = a == null || Number.isNaN(a); const nb = b == null || Number.isNaN(b);
  if (na || nb) return na === nb ? 0 : na ? 1 : -1;
  return order === 'desc' ? b - a : a - b;
}

/** One page of sites ordered by a calculated attribute: the filtered set's ids and values (1000 per read), ordered
 *  here, then the page's rows read by record id. */
async function calculatedSortPage(filter: NomosFilter | undefined, field: string, order: 'asc' | 'desc', page: number,
  page_size: number, period: { start: string; end: string }): Promise<NomosPage> {
  const all: Array<{ id: string; v: number | undefined }> = [];
  for (let offset = 0; ; offset += 1000) {
    const p = await queryRecords('site', { filter, fields: [field], limit: 1000, cursor: offset, period });
    all.push(...p.rows.map((r) => ({ id: r.record_id, v: num(scalar(r.values[field])) })));
    if (p.rows.length < 1000) break;
  }
  all.sort((x, y) => compareNumbersNullsLast(x.v, y.v, order));
  const offset = (page - 1) * page_size;
  const ids = all.slice(offset, offset + page_size).map((x) => x.id);
  if (!ids.length) return { rows: [], total: all.length, limit: page_size, offset };
  const got = await queryRecords('site', { filter: { attribute: 'record_id', op: 'in', value: ids }, fields: LIST_FIELDS, limit: ids.length, period });
  const byId = new Map(got.rows.map((r) => [r.record_id, r]));
  return { rows: ids.flatMap((id) => byId.get(id) ?? []), total: all.length, limit: page_size, offset };
}

/** GET /buildings -> site records. active_only cannot be honoured: NomOS site has no is_active (gap, see report). */
/** The reference lookups every Building row needs, started together with the page's own read (one round trip). */
function prefetchLookups(): void {
  for (const p of [countryCodes(), climateZoneCodes(), co2PerKwh(null)] as Promise<unknown>[]) p.catch(() => undefined);
}

async function getBuildings(params?: GetBuildingsParams): Promise<BuildingListResponse> {
  prefetchLookups();
  const counts = siteCounts();
  counts.catch(() => undefined);
  const { res, page, page_size } = await listSites(params);
  const x = await pageExtras(res.rows, counts);
  const data = await Promise.all(res.rows.map((r) => toBuilding(r, x)));
  const total_pages = Math.max(1, Math.ceil(res.total / page_size)); // pagination arithmetic, not an aggregate
  return { data, total: res.total, page, page_size, total_pages,
    pagination: { total: res.total, page, page_size, total_pages } } as BuildingListResponse;
}

async function getBuilding(id: string): Promise<Building> {
  prefetchLookups();
  const counts = siteCounts(id);
  counts.catch(() => undefined);
  const res = await queryRecords('site', {
    filter: { attribute: 'sentinel_building_id', op: 'eq', value: id }, fields: LIST_FIELDS, limit: 1, period: await euiPeriod() });
  if (!res.rows[0]) {
    // A site created on NomOS has no Sentinel id: its building id IS its record id.
    if (UUID_RE.test(id)) { const own = await buildingByRecordId(id).catch(() => null); if (own) return own; }
    throw toAxiosError(404, { detail: `Building ${id} not found` }, `/buildings/${id}`);
  }
  return toBuilding(res.rows[0], await pageExtras(res.rows, counts));
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** One site by NomOS record id, as a Building (the write paths answer with this). 404 when absent or archived. */
export async function buildingByRecordId(recordId: string): Promise<Building> {
  const period = await euiPeriod();
  const [vr, ids] = await Promise.all([
    nomosFetch<NomosEnvelope<{ values?: Record<string, Record<string, unknown>> }>>('POST', '/objects/site/records/values',
      { record_ids: [recordId], attributes: LIST_FIELDS, period_start: period.start, period_end: period.end }),
    attributeIds('site'),
  ]);
  const cells = vr.data?.values?.[recordId];
  if (!cells || !Object.keys(cells).length) throw toAxiosError(404, { detail: `Building ${recordId} not found` }, `/buildings/${recordId}`);
  const slugOf = Object.fromEntries(Object.entries(ids).map(([slug, aid]) => [aid, slug]));
  const values = Object.fromEntries(Object.entries(cells).map(([aid, v]) => [slugOf[aid] ?? aid, v]));
  const row: NomosRow = { record_id: recordId, kenning: (scalar(values.name) as string) ?? recordId, values };
  return toBuilding(row, await pageExtras([row], siteCounts(scalar(values.sentinel_building_id) as string | undefined ?? undefined)));
}

/** GET /buildings/index -> every site, identity columns only. */
async function getBuildingIndex(): Promise<BuildingIndexResponse> {
  const countries = await countryCodes();
  const rows: NomosRow[] = [];
  let total = 0;
  for (let offset = 0; ; offset += 1000) {
    const res = await queryRecords('site', { fields: ['name', 'building_type', 'location', 'country_ref', 'sentinel_building_id'],
      filter: ACTIVE, limit: 1000, cursor: offset, sorts: [{ field: 'name', direction: 'asc' }] });
    total = res.total; rows.push(...res.rows);
    if (rows.length >= total || res.rows.length === 0) break;
  }
  const data = rows.map((r) => {
    const v = r.values;
    const sid = scalar(v.sentinel_building_id);
    const loc = (Array.isArray(v.location) ? v.location[0] : v.location) as Record<string, unknown> | null | undefined;
    const c = refs(v.country_ref)[0];
    return { id: typeof sid === 'string' ? sid : r.record_id, name: (scalar(v.name) as string) ?? r.kenning,
      country_code: (loc?.country as string) ?? (c ? countries.get(c.id) : null) ?? null,
      building_type: (scalar(v.building_type) as string) ?? null,
      street_address: (loc?.street as string) ?? null, postal_code: (loc?.postal_code as string) ?? null, city: (loc?.city as string) ?? null };
  });
  return { data, total } as unknown as BuildingIndexResponse;
}

/** GET /buildings/{id}/deletion-summary. device_count = metering/actuating assets; readings = NomOS readings/count over all time. */
async function getBuildingDeletionSummary(id: string) {
  const res = await queryRecords('site', { filter: { attribute: 'sentinel_building_id', op: 'eq', value: id },
    fields: ['name', 'shelly_configured'], limit: 1 });
  const r = res.rows[0];
  if (!r) throw toAxiosError(404, { detail: `Building ${id} not found` }, `/buildings/${id}/deletion-summary`);
  const [dev, ini, readings] = await Promise.all([
    groupRecords('asset', { groupBy: ['site'], filter: { and: [{ attribute: 'role', op: 'in', value: ['metering', 'actuating'] }, { attribute: 'site', op: 'eq', value: r.record_id }] } }),
    groupRecords('initiative', { groupBy: ['site'], filter: { attribute: 'site', op: 'eq', value: r.record_id } }),
    nomosFetch<NomosEnvelope<{ rows: Array<{ site: string; reading_count: number }> }>>('POST', '/readings/count', { quantity: 'kwh', site_ids: [r.record_id] }),
  ]);
  return {
    building_id: id,
    building_name: (scalar(r.values.name) as string) ?? r.kenning,
    device_count: dev[0]?.count ?? 0,
    energy_readings_count: readings.data?.rows?.find((x) => x.site === r.record_id)?.reading_count ?? 0,
    initiatives_count: ini[0]?.count ?? 0,
    has_shelly_connection: bool(r.values.shelly_configured) ?? false,
  } as unknown as { building_id: string; building_name: string; device_count: number; energy_readings_count: number;
    initiatives_count: number; has_shelly_connection: boolean };
}

export const buildingsImpl: NomosRegistry = {
  getBuildings: getBuildings as unknown as AnyFn,
  getBuilding: getBuilding as unknown as AnyFn,
  getBuildingIndex: getBuildingIndex as unknown as AnyFn,
  getBuildingDeletionSummary: getBuildingDeletionSummary as unknown as AnyFn,
};
